<script setup lang="ts">
import { computed, reactive, ref } from "vue"
import AppShell from "@/pages/Layout/AppShell.vue"
import { UiAutocompleteSelect, UiButton, UiCnpjInput, UiCpfInput, UiCurrencyInput, UiDataTable, UiPasswordInput, UiSelect, UiTextInput } from "@/components"
import type { UiDataTableColumn, UiDataTableFilter, UiOption } from "@/types/ui"

type ProductRow = {
  sku: string
  name: string
  category: string
  status: string
  price: string
}

const form = reactive({
  email: "compras@fabrik.com",
  password: "",
  cpf: "",
  cnpj: "",
  amount: "0",
  category: "",
  product: null as string | null,
})

const categoryOptions: UiOption[] = [
  { label: "Materia-prima", value: "raw" },
  { label: "Intermediario", value: "intermediate" },
  { label: "Produto final", value: "finished" },
]

const productOptions: UiOption[] = [
  { label: "Chapa de aco", value: "steel-sheet" },
  { label: "Parafuso M8", value: "bolt-m8" },
  { label: "Conjunto de caixa", value: "gearbox-kit" },
  { label: "Motor AC 2cv", value: "ac-motor-2cv" },
]

const rows = ref<ProductRow[]>([
  { sku: "MAT-001", name: "Chapa de aco", category: "Materia-prima", status: "Ativo", price: "R$ 31,90" },
  { sku: "MAT-007", name: "Parafuso M8", category: "Materia-prima", status: "Ativo", price: "R$ 0,48" },
  { sku: "INT-011", name: "Conjunto de caixa", category: "Intermediario", status: "Ativo", price: "R$ 124,99" },
  { sku: "FIN-204", name: "Motor AC 2cv", category: "Produto final", status: "Inativo", price: "R$ 1.999,90" },
  { sku: "FIN-211", name: "Painel de comando", category: "Produto final", status: "Ativo", price: "R$ 2.349,00" },
])

const tableColumns: UiDataTableColumn[] = [
  { key: "sku", label: "SKU" },
  { key: "name", label: "Produto" },
  { key: "category", label: "Categoria" },
  { key: "status", label: "Status" },
  { key: "price", label: "Preco", align: "right" },
]

const tableFilters: UiDataTableFilter[] = [
  {
    key: "status",
    label: "Status",
    options: [
      { label: "Ativo", value: "Ativo" },
      { label: "Inativo", value: "Inativo" },
    ],
  },
]

const search = ref("")
const selectedFilters = reactive<Record<string, string | number | "">>({ status: "" })
const page = ref(1)
const perPage = ref(10)

const filteredRows = computed(() => {
  const normalizedSearch = search.value.toLowerCase().trim()

  return rows.value.filter((row) => {
    const matchesSearch =
      !normalizedSearch ||
      row.sku.toLowerCase().includes(normalizedSearch) ||
      row.name.toLowerCase().includes(normalizedSearch)

    const matchesStatus = !selectedFilters.status || row.status === selectedFilters.status

    return matchesSearch && matchesStatus
  })
})

const paginatedRows = computed(() => {
  const start = (page.value - 1) * perPage.value
  return filteredRows.value.slice(start, start + perPage.value)
})

const pagination = computed(() => ({
  page: page.value,
  perPage: perPage.value,
  total: filteredRows.value.length,
}))

function setSearch(value: string) {
  search.value = value
  page.value = 1
}

function setFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  page.value = 1
}

function setPage(nextPage: number) {
  page.value = Math.max(1, nextPage)
}

function setPerPage(value: number) {
  perPage.value = value
  page.value = 1
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">UI/ComponentsIndex</h1>
        <p class="mt-2 text-sm text-slate-600">Catalogo base para formularios, acoes e tabela com busca/filtros/paginacao.</p>
      </section>

      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-slate-900">Inputs padrao</h2>

        <form class="grid gap-4 md:grid-cols-2">
          <UiTextInput id="email" v-model="form.email" type="email" label="E-mail" placeholder="nome@empresa.com" />

          <UiPasswordInput
            id="password"
            v-model="form.password"
            label="Senha"
            placeholder="Digite a senha"
            helper-text="Use no minimo 8 caracteres"
          />

          <UiCpfInput id="cpf" v-model="form.cpf" />

          <UiCnpjInput id="cnpj" v-model="form.cnpj" />

          <UiCurrencyInput id="amount" v-model="form.amount" label="Valor de custo" helper-text="Valor interno em centavos" />

          <UiSelect id="category" v-model="form.category" label="Categoria" :options="categoryOptions" />

          <UiAutocompleteSelect
            id="product"
            v-model="form.product"
            label="Produto (autocomplete)"
            :options="productOptions"
            helper-text="Busca local para listas curtas"
          />
        </form>
      </section>

      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-slate-900">Botoes padrao</h2>
        <div class="flex flex-wrap gap-3">
          <UiButton variant="primary">Salvar</UiButton>
          <UiButton variant="secondary">Cancelar</UiButton>
          <UiButton variant="ghost">Voltar</UiButton>
          <UiButton variant="danger">Excluir</UiButton>
        </div>
      </section>

      <section>
        <UiDataTable
          :columns="tableColumns"
          :rows="paginatedRows"
          :search="search"
          :filters="tableFilters"
          :selected-filters="selectedFilters"
          :pagination="pagination"
          @update:search="setSearch"
          @update:filter="setFilter"
          @page-change="setPage"
          @per-page-change="setPerPage"
        />
      </section>
    </div>
  </AppShell>
</template>

